import { apiFetch } from './client.js'

function normalizeScheduler(scheduler) {
  if (!scheduler || typeof scheduler !== 'object') {
    return null
  }

  return {
    id: scheduler.id,
    groupConfigId: scheduler.group_config_id ?? scheduler.groupConfigId ?? null,
    elementId: scheduler.element_id ?? scheduler.elementId ?? null,
    name: scheduler.name || '',
    status: scheduler.status || 'Draft',
    startDate: scheduler.start_date || scheduler.startDate || '',
    endDate: scheduler.end_date || scheduler.endDate || '',
    outputFormatTime: scheduler.output_format_time || scheduler.outputFormatTime || 'timestamp',
    duration: scheduler.duration ?? null,
    isDefault: Boolean(scheduler.is_default ?? scheduler.isDefault),
    isFrozen: Boolean(scheduler.is_frozen ?? scheduler.isFrozen),
    createdAt: scheduler.created_at || scheduler.createdAt || null,
  }
}

function extractList(payload) {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.results)) return payload.results
  if (Array.isArray(payload?.data)) return payload.data
  return []
}

function extractItem(payload) {
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    if (payload.item) return payload.item
    if (payload.data && !Array.isArray(payload.data)) return payload.data
  }
  return payload
}

export async function listSchedulers(applicationId) {
  const payload = await apiFetch(`/apps/${encodeURIComponent(applicationId)}/schedulers`)
  return extractList(payload).map(normalizeScheduler).filter(Boolean)
}

export async function getScheduler(applicationId, schedulerId) {
  const payload = await apiFetch(
    `/apps/${encodeURIComponent(applicationId)}/schedulers/${encodeURIComponent(schedulerId)}`,
  )
  return normalizeScheduler(extractItem(payload))
}

export async function createScheduler(applicationId, values) {
  const payload = await apiFetch(`/apps/${encodeURIComponent(applicationId)}/schedulers`, {
    method: 'POST',
    body: {
      group_config_id: Number(values?.groupConfigId),
      element_id: Number(values?.elementId),
      name: String(values?.name || '').trim(),
      start_date: String(values?.startDate || '').trim(),
      end_date: String(values?.endDate || '').trim(),
      output_format_time: String(values?.outputFormatTime || 'timestamp').trim(),
      duration: values?.duration == null || values?.duration === '' ? null : Number(values.duration),
      is_default: Boolean(values?.isDefault),
      is_frozen: Boolean(values?.isFrozen),
    },
  })
  return normalizeScheduler(extractItem(payload))
}

export async function setSchedulerStatus(applicationId, schedulerId, status) {
  const payload = await apiFetch(
    `/apps/${encodeURIComponent(applicationId)}/schedulers/${encodeURIComponent(schedulerId)}/status`,
    {
      method: 'POST',
      body: { status: String(status || '').trim() },
    },
  )
  return normalizeScheduler(extractItem(payload))
}

export async function updateScheduler(applicationId, schedulerId, values) {
  const payload = await apiFetch(
    `/apps/${encodeURIComponent(applicationId)}/schedulers/${encodeURIComponent(schedulerId)}`,
    {
      method: 'PATCH',
      body: {
        ...(Object.prototype.hasOwnProperty.call(values || {}, 'groupConfigId')
          ? { group_config_id: values.groupConfigId == null ? null : Number(values.groupConfigId) }
          : {}),
        ...(Object.prototype.hasOwnProperty.call(values || {}, 'elementId')
          ? { element_id: values.elementId == null ? null : Number(values.elementId) }
          : {}),
        ...(Object.prototype.hasOwnProperty.call(values || {}, 'name')
          ? { name: String(values.name || '').trim() }
          : {}),
        ...(Object.prototype.hasOwnProperty.call(values || {}, 'startDate')
          ? { start_date: String(values.startDate || '').trim() }
          : {}),
        ...(Object.prototype.hasOwnProperty.call(values || {}, 'endDate')
          ? { end_date: String(values.endDate || '').trim() }
          : {}),
        ...(Object.prototype.hasOwnProperty.call(values || {}, 'outputFormatTime')
          ? { output_format_time: String(values.outputFormatTime || '').trim() }
          : {}),
        ...(Object.prototype.hasOwnProperty.call(values || {}, 'duration')
          ? {
              duration:
                values.duration == null || values.duration === '' ? null : Number(values.duration),
            }
          : {}),
        ...(Object.prototype.hasOwnProperty.call(values || {}, 'isDefault')
          ? { is_default: Boolean(values.isDefault) }
          : {}),
        ...(Object.prototype.hasOwnProperty.call(values || {}, 'isFrozen')
          ? { is_frozen: Boolean(values.isFrozen) }
          : {}),
      },
    },
  )
  return normalizeScheduler(extractItem(payload))
}

export async function checkSchedulerDefault(applicationId, groupConfigId, elementId) {
  const params = new URLSearchParams({
    group_config_id: String(groupConfigId),
    element_id: String(elementId),
  })
  const payload = await apiFetch(
    `/apps/${encodeURIComponent(applicationId)}/schedulers/check-default?${params}`,
  )
  return {
    hasSchedulers: Boolean(payload?.has_schedulers ?? payload?.hasSchedulers),
    hasDefault: Boolean(payload?.has_default ?? payload?.hasDefault),
  }
}

export async function deleteScheduler(applicationId, schedulerId) {
  await apiFetch(`/apps/${encodeURIComponent(applicationId)}/schedulers/${encodeURIComponent(schedulerId)}`, {
    method: 'DELETE',
  })
}
